import React from "react";
import * as AlertPrimitive from "@radix-ui/react-alert-dialog";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { PORTAL_SURFACE } from "./classes";
import CrmButton from "./CrmButton";

/**
 * A confirmation that must be answered (Radix AlertDialog: no dismiss on outside click).
 * Cancel is the admin text link; the confirm button is the primary pill, rose when
 * `tone="danger"`. `confirmDisabled` holds the confirm (e.g. until a typed name matches).
 */
export default function CrmAlertDialog({
  open, onOpenChange, title, description, children, confirmLabel, cancelLabel,
  onConfirm, tone = "primary", busy = false, confirmDisabled = false, testId, confirmTestId, cancelTestId,
}) {
  const t = useT("crm");
  return (
    <AlertPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <AlertPrimitive.Content
          data-testid={testId}
          data-lenis-prevent
          className={cn(
            PORTAL_SURFACE,
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6",
            "max-h-[calc(100vh-2rem)] overflow-y-auto",
          )}
        >
          <AlertPrimitive.Title className="font-display text-2xl leading-tight">{title}</AlertPrimitive.Title>
          {description
            ? <AlertPrimitive.Description className="mt-3 text-sm text-eh-ink">{description}</AlertPrimitive.Description>
            : <AlertPrimitive.Description className="sr-only">{title}</AlertPrimitive.Description>}
          {children && <div className="mt-4 text-sm">{children}</div>}
          <div className="mt-6 flex items-center justify-end gap-4">
            <AlertPrimitive.Cancel asChild>
              <CrmButton variant="link" data-testid={cancelTestId}>{cancelLabel || t("common.cancel")}</CrmButton>
            </AlertPrimitive.Cancel>
            <CrmButton
              variant={tone === "danger" ? "danger" : "primary"}
              disabled={busy || confirmDisabled}
              onClick={onConfirm}
              data-testid={confirmTestId}
            >
              {confirmLabel}
            </CrmButton>
          </div>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  );
}
